import { signal } from '@preact/signals-react';
import { ConsentStatusEnum } from '@pypestream/api-services';
import {
  resolveSessionAtBoot,
  sessionService,
  watchSessionVisibility,
} from '@pypestream/api-services/session';
import {
  Button,
  Page,
  PageBody,
  Result,
  Spinner,
  Stack,
} from '@pypestream/design-system';
import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';

import { identifyUser, removeTrailingSlash } from '@pypestream/utils';
import { useAnalytics } from './hooks';
import {
  sendGlobalAppEvent,
  sendUserEvent,
  useManagerCtxSelector,
  useUserCtxSelector,
} from './xstate/app.xstate';

export type WindowProduktly = Window &
  typeof globalThis & {
    Produktly: {
      identifyUser: (
        userId?: string,
        metadata?: Record<string, unknown>
      ) => void;
    };
  };

/**
 * PSTUDIO-4022. `unavailable` is deliberately not a sign-out: Kratos being
 * unreachable says nothing about the session, and the previous boot code signed
 * the user out on any failed session read.
 */
type SessionBootState = 'checking' | 'active' | 'expired' | 'unavailable';

const sessionState = signal<SessionBootState>('checking');

// The only place that turns "session gone" into a sign-out, so a burst of failed
// operations cannot start several Kratos logout flows. `hadActiveSession` gates
// the `?sessionEnded=true` the login page explains itself with: Kratos answers
// 401 both for an expired session and for a visitor who never had one, and only
// the first is a session that ended.
sessionService.onExpired(({ hadActiveSession }) => {
  sessionState.value = 'expired';
  sendUserEvent('user.signOut', { isSessionEndedEvent: hadActiveSession });
});

resolveSessionAtBoot({ session: sessionService })
  .then((status) => {
    // The urql auth exchange revalidates independently, so it can reach
    // `expired` while the boot read is still retrying an inconclusive answer —
    // and a sign-out already in flight must not be painted over.
    if (sessionState.value !== 'checking') return;

    if (status.state === 'active') {
      sessionState.value = 'active';

      sendGlobalAppEvent({
        type: 'user.signIn',
        user: {
          // I'm trying to avoid passing in this info, only to have it immediately get overwritten
          // by our getCurrentUser GQL query
          kratosId: status.identityId,
        },
      });

      return;
    }

    if (status.state === 'unknown') {
      console.error('Could not reach Kratos to verify the session at boot');
      sessionState.value = 'unavailable';
    }

    // `expired` is handled by the onExpired subscriber above.
  })
  .catch((error) => {
    if (sessionState.value !== 'checking') return;

    // resolveSessionAtBoot never rejects; belt and braces so a boot-time throw
    // cannot leave the app with no session state at all.
    console.error('Session error:', error);
    sessionState.value = 'unavailable';
  });

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  const analytics = useAnalytics();

  const { pathname } = window.location;

  const pathAsArray = removeTrailingSlash(pathname).split('/').filter(Boolean);
  const isHomePage = pathname === '/' || pathAsArray.length === 2;

  const { optionalConsentStatus, userId, userEmail, userName } =
    useUserCtxSelector((ctx) => ({
      optionalConsentStatus: ctx.user?.optionalConsentStatus,
      userId: ctx.user?.id,
      userEmail: ctx.user?.email,
      userName:
        ctx.user?.firstName && ctx.user?.lastName
          ? `${ctx.user.firstName} ${ctx.user.lastName}`
          : undefined,
    }));

  const { enablePostHogSessionRecording } = useManagerCtxSelector((ctx) => ({
    enablePostHogSessionRecording:
      ctx.featureFlags?.['post-hog-session-recording'] ?? false,
  }));

  // See the twin in pro-studio: without `setUser`, Sentry counts impacted
  // users by IP address and nothing about the person is searchable.
  useEffect(() => {
    identifyUser(userId ? { id: userId, email: userEmail } : null);
  }, [userId, userEmail]);

  useEffect(() => {
    if (!optionalConsentStatus || !analytics) {
      if (!optionalConsentStatus) {
        console.log('>>>>>>> Analytics: Waiting for consent status');
      }
      if (!analytics) {
        console.log('>>>>>>> Analytics: Analytics instance not available');
      }
      return;
    }

    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      if (typeof analytics.opt_in_capturing === 'function') {
        analytics.opt_in_capturing();
      }

      if (userId && typeof analytics.identify === 'function') {
        analytics.identify(userId, {
          email: userEmail,
          name: userName,
          app: 'manager',
        });
      }
    } else {
      if (typeof analytics.opt_out_capturing === 'function') {
        analytics.opt_out_capturing();
      }
    }
  }, [optionalConsentStatus, analytics, userEmail, userId, userName]);

  // Identify the user to Produktly regardless of consent so CSAT/NPS feedback
  // is never reported as "User: null" (consent only gates PostHog/Sentry) — PSTUDIO-3329
  useEffect(() => {
    if (!userId) return;

    (window as WindowProduktly).Produktly?.identifyUser(userId, {
      email: userEmail,
      name: userName,
    });
  }, [userEmail, userId, userName]);

  useEffect(() => {
    if (!optionalConsentStatus) return;

    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      console.log('start capturing');
    } else {
      Sentry.close().then(function () {
        console.log('opting out of Sentry');
      });
    }
  }, [optionalConsentStatus]);

  useEffect(() => {
    if (
      !analytics ||
      typeof analytics.startSessionRecording !== 'function' ||
      typeof analytics.stopSessionRecording !== 'function' ||
      typeof analytics.sessionRecordingStarted !== 'function'
    )
      return;

    if (
      optionalConsentStatus === ConsentStatusEnum.Accepted &&
      enablePostHogSessionRecording &&
      typeof analytics.startSessionRecording === 'function' &&
      typeof analytics.stopSessionRecording === 'function'
    ) {
      if (typeof analytics.startSessionRecording === 'function') {
        analytics.startSessionRecording();
      }
    } else {
      if (typeof analytics.stopSessionRecording === 'function') {
        analytics.stopSessionRecording();
      }
    }
  }, [enablePostHogSessionRecording, optionalConsentStatus, analytics]);

  // An idle tab is exactly how PSTUDIO-4022 was reported: nothing re-checks the
  // session, so the first thing the user sees on return is a broken screen.
  useEffect(() => watchSessionVisibility({ session: sessionService }), []);

  if (sessionState.value === 'unavailable') {
    return (
      <Page>
        <PageBody background="none">
          <Result
            align="center"
            status="error"
            label="We couldn't verify your session"
            body="The sign-in service could not be reached. Check your connection and try again."
          >
            <Stack gutter="xsmall" justifyContent="center" slot="footer">
              <Button size="large" onClick={() => window.location.reload()}>
                Try again
              </Button>
            </Stack>
          </Result>
        </PageBody>
      </Page>
    );
  }

  // A sign-out is a redirect, so `expired` keeps the loader up rather than
  // flashing an error on the way out.
  if (sessionState.value !== 'active') {
    return (
      <Page>
        <PageBody background="none">
          <Spinner fullpage />
        </PageBody>
      </Page>
    );
  }

  // todo: do we need this?
  // const splittedPath = window.location.href.split('?');
  // window.history.pushState('authenticated', '', splittedPath[0]);
  return <>{children}</>;
};
